/** biome-ignore-all lint/correctness/useUniqueElementIds: unique login component */
/** biome-ignore-all lint/a11y/useValidAnchor: custom link */
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ButtonUI, Input, showNotification, useQueryFetch, validationErrorChecker } from 'sonata-react-components';

import { sendPasswordResetEmail } from '@/services/authentication.service';
import { type ForgotPasswordFormValues, getForgotPasswordSchema } from '@/validators/user.validator';

import './styles.scss';

type State = ForgotPasswordFormValues;

export default function ForgotPasswordPage() {
  const { formatMessage } = useIntl();
  const forgotPasswordSchema = getForgotPasswordSchema(formatMessage);
  const navigate = useNavigate();
  const [{ email }, setState] = useState<State>({
    email: ''
  });

  const { isFetching, refetch } = useQueryFetch(
    [email],
    async () => {
      await sendPasswordResetEmail(email);
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'If the email exists in our system, a reset link has been sent.',
          id: 'forgotPassword.successNotification'
        })
      );
    },
    {
      enabled: false,
      onError: () => {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Error sending password reset email',
            id: 'forgotPassword.errorNotification'
          })
        );
      },
      staleTime: 0
    }
  );

  const handleEmailChange = (newEmail: string) => {
    setState(prevState => ({ ...prevState, email: newEmail }));
  };

  const handleSend = () => {
    refetch();
  };

  const handleCancelClick = () => {
    navigate('/login');
  };

  const errors = validationErrorChecker({ email }, forgotPasswordSchema);

  return (
    <div className="forgotPassword">
      <h1 className="forgotPassword__title">
        {formatMessage({
          defaultMessage: 'Forgot your password?',
          id: 'forgotPassword.title'
        })}
      </h1>

      <form className="forgotPassword__form">
        <span className="forgotPassword__emailContainer">
          <span>
            {formatMessage({
              defaultMessage: 'Which email should we use to send your reset link?',
              id: 'forgotPassword.emailInstructions'
            })}
          </span>
          <Input
            id="email"
            inputAdditionalProps={{
              autoComplete: 'email'
            }}
            label={formatMessage({
              defaultMessage: 'Email',
              id: 'forgotPassword.emailLabel'
            })}
            name="email"
            onChange={textInput => handleEmailChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter your email',
              id: 'forgotPassword.emailPlaceholder'
            })}
            shape="squareUI"
            type="text"
            value={email}
          />
        </span>
        <div className="forgotPassword__actions">
          <ButtonUI
            className="forgotPassword__cancelButton"
            id="forgotPassword-cancelButton"
            onClick={handleCancelClick}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'common.cancel'
            })}
            type="secondary"
          />
          <ButtonUI
            className="forgotPassword__sendButton"
            disabled={Object.keys(errors || {}).length > 0}
            icon="fa-regular fa-paper-plane"
            id="forgotPassword-sendButton"
            loading={isFetching}
            onClick={handleSend}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Send',
              id: 'forgotPassword.sendButton'
            })}
            type="primary"
          />
        </div>
      </form>
    </div>
  );
}
